import React, { useEffect, useState } from 'react';
import { CheckCircle2, Copy, Plug } from 'lucide-react';
import { getSubscriberMcpAccess, type SubscriberMcpAccess } from '../api.js';

/**
 * One-click Grok Bot (Cursor) registration for a subscriber.
 *
 * The button installs a subscriber-only MCP server entry in Cursor via its
 * install deep link. On first use Cursor runs OAuth, which hands the browser
 * to this portal (already signed in) for a single Approve. The server entry
 * uses its own name and, once MC serves it, its own URL, so it never shares
 * stored credentials with an internal desk connector on the same machine.
 */
export const SUBSCRIBER_SERVER_NAME = 'ocnexus-forge';
export const SUBSCRIBER_MCP_URL: string =
  (import.meta.env.VITE_OCN_SUBSCRIBER_MCP_URL as string | undefined) || 'https://mcp.ocnexus.dev/subscriber';
const SUBSCRIBER_CLIENT = 'grok-subscriber';
const SUBSCRIBER_SCOPES: readonly string[] = [
  'ocn:profile:read', 'ocn:repo:read', 'ocn:forge:read', 'ocn:forge:submit', 'ocn:usage:read',
];

export function subscriberServerConfig(): Record<string, unknown> {
  return { url: SUBSCRIBER_MCP_URL, auth: { CLIENT_ID: SUBSCRIBER_CLIENT, scopes: SUBSCRIBER_SCOPES } };
}

function base64(value: string): string {
  if (typeof btoa === 'function') return btoa(value);
  return (globalThis as unknown as { Buffer: { from(v: string): { toString(e: string): string } } }).Buffer.from(value).toString('base64');
}

export function cursorInstallLink(): string {
  const config = base64(JSON.stringify(subscriberServerConfig()));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(SUBSCRIBER_SERVER_NAME)}&config=${encodeURIComponent(config)}`;
}

const POLL_MS = 8000;

/**
 * Where 'Add to Grok Bot' sends the subscriber: the published ocnexus-forge
 * plugin install page. Until that exists, the button must NOT fire the raw
 * cursor:// deep link -- on a machine without Cursor desktop the browser shows
 * an unbranded external-protocol prompt and then nothing happens (canary
 * 2026-10-02).
 */
export const GROK_INSTALL_URL: string | undefined =
  (import.meta.env.VITE_OCN_GROK_INSTALL_URL as string | undefined) || undefined;

export default function GrokBotConnect({ installUrl = GROK_INSTALL_URL }: { installUrl?: string } = {}): React.ReactElement {
  const [access, setAccess] = useState<SubscriberMcpAccess | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async (): Promise<void> => {
      try {
        const next = await getSubscriberMcpAccess();
        if (!alive) return;
        setAccess(next);
        if (next.provisioning_status !== 'blocked' && !next.assistant_connected) timer = setTimeout(load, POLL_MS);
      } catch {
        if (alive) timer = setTimeout(load, POLL_MS * 2);
      }
    };
    void load();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  const ready = access?.provisioning_status === 'ready';
  const installable = ready && Boolean(installUrl);
  const connected = Boolean(access?.assistant_connected);
  const connectedAt = access?.assistant_connected_at ? new Date(access.assistant_connected_at) : null;

  const copyConfig = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ mcpServers: { [SUBSCRIBER_SERVER_NAME]: subscriberServerConfig() } }, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable: the button is a convenience only */ }
  };

  return <section className="card" aria-labelledby="grok-connect-heading" style={{ padding: 18, margin: 0 }}>
    <div className="section-label">Grok Bot</div>
    <h3 id="grok-connect-heading" style={{ margin: '4px 0 8px' }}>
      {connected ? 'Grok Bot is connected.' : 'Connect Grok Bot in one click.'}
    </h3>
    {connected ? <p style={{ margin: '0 0 12px' }}>
      <CheckCircle2 size={14} /> Approved{connectedAt ? ` ${connectedAt.toLocaleString()}` : ''}. Ask Grok to run a Forge review on one of your repositories.
    </p> : installable ? <p style={{ margin: '0 0 12px' }}>
      Click <strong>Add to Grok Bot</strong>, then approve once in this portal and you are done.
    </p> : <p style={{ margin: '0 0 12px' }}>
      {ready ? 'The Grok Bot install is being published. This button turns on as soon as it is live.' : 'Available as soon as your workspace setup finishes.'}
    </p>}
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {installable
        ? <a className="btn btn-primary" href={installUrl} target="_blank" rel="noreferrer"><Plug size={14} /> {connected ? 'Reinstall in Grok Bot' : 'Add to Grok Bot'}</a>
        : <button type="button" className="btn btn-primary" disabled><Plug size={14} /> Add to Grok Bot</button>}
      <button type="button" className="btn" onClick={(): void => { void copyConfig(); }} disabled={!ready}>
        <Copy size={14} /> {copied ? 'Copied' : 'Copy setup for another app'}
      </button>
    </div>
    {!ready && null}
  </section>;
}
